import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { OrderForm } from './transformToEditOrderRequestObject';
import dayjs from 'dayjs';
import { isSameDate, isTimeString } from '../../../../utils/date';

export default function EditDeliveryFromUntilDate({
  deliveryFromDate,
  deliveryUntilDate,
  updateOrderForm,
}: {
  deliveryFromDate: string;
  deliveryUntilDate: string;
  updateOrderForm: (o: Partial<OrderForm>) => void;
}): JSX.Element {
  const [selectedFromDate, setSelectedFromDate] = useState<Date>(
    dayjs(deliveryFromDate).toDate()
  );
  const [selectedUntilDate, setSelectedUntilDate] = useState<Date>(
    dayjs(deliveryUntilDate).toDate()
  );

  const windowTimeRanges = [
    '17:30-19:30',
    '19:30-21:30',
    '17:30-21:30',
    '19:00-22:00',
  ];

  function setSelectedTimeRange(rangeString: string) {
    const [from, until] = rangeString.split(/-/, 2);
    const baseDate = selectedFromDate; // Use date from "from" to make sure both ranges are on the same day

    const [fromH, fromM] = from.split(/:/, 2);
    const fromDate = dayjs(baseDate)
      .hour(parseInt(fromH, 10))
      .minute(parseInt(fromM, 10))
      .toDate();
    setSelectedFromDate(fromDate);

    const [untilH, untilM] = until.split(/:/, 2);
    const untilDate = dayjs(baseDate)
      .hour(parseInt(untilH, 10))
      .minute(parseInt(untilM, 10))
      .toDate();
    setSelectedUntilDate(untilDate);

    updateOrderForm({
      deliveryFromDate: fromDate.toISOString(),
      deliveryUntilDate: untilDate.toISOString(),
    });
  }

  const changeDeliveryDate = (newFromDate: Date, newUntilDate: Date) => {
    setSelectedFromDate(newFromDate);
    setSelectedUntilDate(newUntilDate);
    updateOrderForm({
      deliveryFromDate: newFromDate.toISOString(),
      deliveryUntilDate: newUntilDate.toISOString(),
    });
  };

  const changeToNextDay = () => {
    const newFromDate = dayjs(deliveryFromDate).add(1, 'day').toDate();
    const newUntilDate = dayjs(deliveryUntilDate).add(1, 'day').toDate();
    changeDeliveryDate(newFromDate, newUntilDate);
  };

  const changeToToday = () => {
    const oldFromDate = dayjs(deliveryFromDate);
    const oldUntilDate = dayjs(deliveryUntilDate);

    const newFromDate = new Date();
    newFromDate.setHours(oldFromDate.get('hour'));
    newFromDate.setMinutes(oldFromDate.get('minute'));
    newFromDate.setSeconds(oldFromDate.get('second'));

    const newUntilDate = new Date();
    newUntilDate.setHours(oldUntilDate.get('hour'));
    newUntilDate.setMinutes(oldUntilDate.get('minute'));
    newUntilDate.setSeconds(oldUntilDate.get('second'));

    changeDeliveryDate(newFromDate, newUntilDate);
  };

  const isToday = () => {
    const today = dayjs().format('DD/MM/YYYY');
    const fromDate = dayjs(deliveryFromDate).format('DD/MM/YYYY');

    return today === fromDate;
  };

  function isSelectedTimeRange(rangeString: string) {
    const [fromTime, untilTime] = rangeString.split(/-/, 2);

    return (
      isTimeString(selectedFromDate, fromTime) &&
      isTimeString(selectedUntilDate, untilTime) &&
      isSameDate(selectedFromDate, selectedUntilDate)
    );
  }

  function synchronizeDate(selectedDate: Date, originalDate: Date): Date {
    return new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      originalDate.getHours(),
      originalDate.getMinutes()
    );
  }

  return (
    <div>
      <span className="box__field">
        <h5>Delivery from:</h5>
        <Flatpickr
          onChange={selectedDates => {
            const newUntilDate = synchronizeDate(
              selectedDates[0],
              selectedUntilDate
            );
            setSelectedFromDate(selectedDates[0]);
            setSelectedUntilDate(newUntilDate);
            updateOrderForm({
              deliveryFromDate: selectedDates[0].toISOString(),
              deliveryUntilDate: newUntilDate.toISOString(),
            });
          }}
          value={selectedFromDate}
          options={{
            dateFormat: 'd.m.y H:i',
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
            time_24hr: true,
            enableTime: true,
          }}
          render={(props, ref) => (
            <input className={'form-control'} type="text" ref={ref} />
          )}
        />
      </span>
      <span className="box__field">
        <h5>Delivery to:</h5>
        <Flatpickr
          onChange={selectedDates => {
            const newFromDate = synchronizeDate(
              selectedDates[0],
              selectedFromDate
            );
            setSelectedFromDate(newFromDate);
            setSelectedUntilDate(selectedDates[0]);
            updateOrderForm({
              deliveryFromDate: newFromDate.toISOString(),
              deliveryUntilDate: selectedDates[0].toISOString(),
            });
          }}
          value={selectedUntilDate}
          options={{
            dateFormat: 'd.m.y H:i',
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
            time_24hr: true,
            enableTime: true,
          }}
          render={(props, ref) => (
            <input className={'form-control'} type="text" ref={ref} />
          )}
        />
      </span>
      <span className="box__field">
        <h5>Quick select:</h5>
        <div className="selector-wrapper">
          <div className="selector inline">
            <div
              className={isToday() ? 'selected' : ''}
              onClick={changeToToday}
            >
              Today
            </div>
            <div onClick={changeToNextDay}>Next day</div>
          </div>
          <div className="selector">
            {windowTimeRanges.map(timeRange => (
              <div
                key={timeRange}
                className={isSelectedTimeRange(timeRange) ? 'selected' : ''}
                onClick={() => setSelectedTimeRange(timeRange)}
              >
                {timeRange}
              </div>
            ))}
          </div>
        </div>
      </span>
    </div>
  );
}
